import React, { useEffect, useState, memo } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useNavigate } from 'react-router-dom';
import { UploadIcon, CheckIcon, XIcon, FileTextIcon, AlertCircleIcon, InfoIcon, SaveIcon, UserIcon, DollarSignIcon, MapPinIcon, GlobeIcon, UserCheckIcon, CameraIcon, PencilIcon, ImageIcon, StarIcon } from 'lucide-react';
export const CompleteTeacherProfile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [teacherName, setTeacherName] = useState('');
  const [teacherEmail, setTeacherEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Profile form state
  const [profileData, setProfileData] = useState({
    fullBio: '',
    yearsExperience: '',
    lessonFormat: [] as string[],
    hourlyRate: '',
    rate45Min: '50',
    rate60Min: '60',
    contactForPricing: false,
    cityState: '',
    selectedInstruments: [] as string[],
    selectedDurations: [] as string[],
    howITeach: '',
    certificates: [] as string[]
  });
  // File states
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null);
  const [certificates, setCertificates] = useState<File[]>([]);
  const [certificatePreviewUrls, setCertificatePreviewUrls] = useState<string[]>([]);
  const [isEditingBanner, setIsEditingBanner] = useState(false);
  const [isEditingProfilePic, setIsEditingProfilePic] = useState(false);
  const [tempProfilePicture, setTempProfilePicture] = useState<File | null>(null);
  const [tempBannerImage, setTempBannerImage] = useState<File | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Add this new state for custom instrument input
  const [customInstrument, setCustomInstrument] = useState('');
  // Default banner image
  const defaultBannerImage = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80';
  // Available instruments for the multi-select dropdown
  const instruments = ['Piano', 'Guitar', 'Violin', 'Viola', 'Cello', 'Drums', 'Flute', 'Clarinet', 'Saxophone', 'Trumpet', 'Voice', 'Music Theory', 'Music Production', 'Composition', 'Bass'];
  // Lesson format options
  const lessonFormatOptions = ['Online', 'In-person', 'Both'];
  useEffect(() => {
    // Check if teacher is logged in
    const storedEmail = localStorage.getItem('teacherEmail');
    const storedName = localStorage.getItem('teacherName');
    if (!storedEmail || !storedName) {
      // If not logged in, redirect to login page
      navigate('/teacher-login');
      return;
    }
    setTeacherName(storedName);
    setTeacherEmail(storedEmail);
    // Try to load existing profile data if available
    const teacherUsers = JSON.parse(localStorage.getItem('teacherUsers') || '[]');
    const currentTeacher = teacherUsers.find((user: any) => user.email.toLowerCase() === storedEmail.toLowerCase());
    if (currentTeacher && currentTeacher.profileData) {
      setProfileData(currentTeacher.profileData);
      // Load profile picture if available
      if (currentTeacher.profilePictureUrl) {
        setProfilePicturePreview(currentTeacher.profilePictureUrl);
      }
      // Load banner image if available
      if (currentTeacher.bannerImageUrl) {
        setBannerImagePreview(currentTeacher.bannerImageUrl);
      }
    } else if (currentTeacher) {
      // Pre-fill with data from signup if available
      setProfileData(prev => ({
        ...prev,
        cityState: currentTeacher.location || '',
        selectedInstruments: currentTeacher.instruments || []
      }));
    }
    setIsLoading(false);
  }, [navigate]);
  // Handle profile picture upload
  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setTempProfilePicture(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      // Revoke previous URL to avoid memory leaks
      if (tempProfilePicture) {
        URL.revokeObjectURL(URL.createObjectURL(tempProfilePicture));
      }
    }
  };
  // Handle banner image upload
  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setTempBannerImage(file);
      // Create preview URL is handled in the render when the temp image exists
    }
  };
  // Handle certificate uploads
  const handleCertificateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setCertificates(prev => [...prev, ...newFiles]);
      // Create preview URLs for the new files
      const newUrls = newFiles.map(file => URL.createObjectURL(file));
      setCertificatePreviewUrls(prev => [...prev, ...newUrls]);
    }
  };
  // Remove a certificate
  const handleRemoveCertificate = (index: number) => {
    setCertificates(prev => prev.filter((_, i) => i !== index));
    // Revoke the object URL to avoid memory leaks
    URL.revokeObjectURL(certificatePreviewUrls[index]);
    setCertificatePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };
  // Handle text input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      id,
      value
    } = e.target;
    setProfileData(prev => ({
      ...prev,
      [id]: value
    }));
  };
  // Handle instrument selection
  const handleInstrumentChange = (instrument: string) => {
    setProfileData(prev => {
      const isSelected = prev.selectedInstruments.includes(instrument);
      let updatedInstruments: string[];
      if (isSelected) {
        // Remove the instrument if already selected
        updatedInstruments = prev.selectedInstruments.filter(i => i !== instrument);
      } else {
        // Add the instrument if not already selected
        updatedInstruments = [...prev.selectedInstruments, instrument];
      }
      return {
        ...prev,
        selectedInstruments: updatedInstruments
      };
    });
  };
  // Add this new function to handle adding custom instruments
  const handleAddCustomInstrument = (e: React.KeyboardEvent<HTMLInputElement> | React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (customInstrument.trim() === '') return;
    // Check if the instrument is already in the list
    if (!profileData.selectedInstruments.includes(customInstrument.trim())) {
      setProfileData(prev => ({
        ...prev,
        selectedInstruments: [...prev.selectedInstruments, customInstrument.trim()]
      }));
    }
    setCustomInstrument('');
  };
  // Add this function to remove instruments
  const handleRemoveInstrument = (instrument: string) => {
    setProfileData(prev => ({
      ...prev,
      selectedInstruments: prev.selectedInstruments.filter(i => i !== instrument)
    }));
  };
  // Handle duration selection
  const handleDurationChange = (duration: string) => {
    setProfileData(prev => {
      const isSelected = prev.selectedDurations.includes(duration);
      let updatedDurations: string[];
      if (isSelected) {
        // Remove the duration if already selected
        updatedDurations = prev.selectedDurations.filter(d => d !== duration);
      } else {
        // Add the duration if not already selected
        updatedDurations = [...prev.selectedDurations, duration];
      }
      return {
        ...prev,
        selectedDurations: updatedDurations
      };
    });
  };
  // Handle lesson format selection
  const handleLessonFormatChange = (format: string) => {
    setProfileData(prev => {
      const isSelected = prev.lessonFormat.includes(format);
      let updatedFormat: string[];
      if (isSelected) {
        // Remove the format if already selected
        updatedFormat = prev.lessonFormat.filter(f => f !== format);
      } else {
        // Add the format if not already selected
        updatedFormat = [...prev.lessonFormat, format];
      }
      return {
        ...prev,
        lessonFormat: updatedFormat
      };
    });
  };
  // Handle drag events for uploads
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        if (isEditingProfilePic) {
          setTempProfilePicture(file);
        } else if (isEditingBanner) {
          setTempBannerImage(file);
        }
      }
    }
  };
  // Ensure the banner text updates when instruments change
  useEffect(() => {
    // This effect will run whenever profileData.selectedInstruments changes
    // The component will re-render and update the banner text
  }, [profileData.selectedInstruments]);
  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (profilePicturePreview && !profilePicturePreview.startsWith('http')) {
        URL.revokeObjectURL(profilePicturePreview);
      }
      if (bannerImagePreview && !bannerImagePreview.startsWith('http')) {
        URL.revokeObjectURL(bannerImagePreview);
      }
      certificatePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);
  // Handle checkbox change for contact for pricing
  const handleContactForPricingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData(prev => ({
      ...prev,
      contactForPricing: e.target.checked
    }));
  };
  // Save profile data
  const saveProfile = () => {
    // Validate form
    if (!profileData.fullBio.trim()) {
      setErrorMessage('Please enter your bio');
      return;
    }
    if (!profileData.yearsExperience.trim()) {
      setErrorMessage('Please enter your years of experience');
      return;
    }
    if (profileData.lessonFormat.length === 0) {
      setErrorMessage('Please select at least one lesson format');
      return;
    }
    if (!profileData.cityState.trim()) {
      setErrorMessage('Please enter your city and state');
      return;
    }
    if (profileData.selectedInstruments.length === 0) {
      setErrorMessage('Please select at least one instrument that you teach');
      return;
    }
    if (profileData.selectedDurations.length === 0) {
      setErrorMessage('Please select at least one lesson duration');
      return;
    }
    // Check if pricing is provided for selected durations when not using contact for pricing
    if (!profileData.contactForPricing) {
      let pricingError = false;
      profileData.selectedDurations.forEach(duration => {
        if (duration === '45' && !profileData.rate45Min) pricingError = true;
        if (duration === '60' && !profileData.rate60Min) pricingError = true;
      });
      if (pricingError) {
        setErrorMessage('Please provide pricing for all selected durations');
        return;
      }
    }
    // Start saving
    setIsSaving(true);
    setErrorMessage('');
    // In a real app, we would upload the profile picture and certificates to a server
    // For this demo, we'll simulate a delay and store the data in localStorage
    setTimeout(() => {
      try {
        // Get existing teacher data
        const teacherUsers = JSON.parse(localStorage.getItem('teacherUsers') || '[]');
        const teacherIndex = teacherUsers.findIndex((user: any) => user.email.toLowerCase() === teacherEmail.toLowerCase());
        if (teacherIndex !== -1) {
          // Update the teacher's profile data
          teacherUsers[teacherIndex] = {
            ...teacherUsers[teacherIndex],
            profileData: profileData,
            profileComplete: true,
            // In a real app, these would be URLs to the uploaded files
            profilePictureUrl: profilePicturePreview,
            bannerImageUrl: bannerImagePreview || defaultBannerImage,
            certificatesCount: certificates.length,
            updatedAt: new Date().toISOString()
          };
          localStorage.setItem('teacherUsers', JSON.stringify(teacherUsers));
          // Show success message
          setSaveSuccess(true);
          // Redirect to profile page after a short delay
          setTimeout(() => {
            navigate('/teacher-profile', {
              state: {
                fromProfileCompletion: true
              }
            });
          }, 1500);
        } else {
          setErrorMessage('Could not find your account. Please try logging in again.');
        }
      } catch (error) {
        console.error('Error saving profile:', error);
        setErrorMessage('An error occurred while saving your profile. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }, 1500); // Simulate network delay
  };
  if (isLoading) {
    return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans flex items-center justify-center" data-id="element-3513">
        <div className="text-center" data-id="element-3514">
          <svg className="animate-spin h-10 w-10 text-white mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3515">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3516"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3517"></path>
          </svg>
          <p data-id="element-3518">Loading your profile...</p>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-3519">
      <Navbar data-id="element-3520" />
      <main className="pt-20 pb-16" data-id="element-3521">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3522">
          {/* Banner and Profile Image Section - Styled like the profile page */}
          <div className="relative mb-8 rounded-xl overflow-hidden" data-id="element-3523">
            {/* Banner Image */}
            <div className="h-64 w-full relative group" data-id="element-3524">
              <img src={bannerImagePreview || defaultBannerImage} alt="Profile banner" className="w-full h-full object-cover" data-id="element-3525" />
              <div className="absolute inset-0 bg-black/40" data-id="element-3526"></div>
              {/* Banner Edit Button */}
              <button onClick={() => setIsEditingBanner(true)} className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 p-2 rounded-full transition-opacity" title="Edit banner image" data-id="element-3527">
                <PencilIcon className="h-5 w-5 text-white" data-id="element-3528" />
              </button>
              {/* Profile Info Overlay */}
              <div className="absolute bottom-6 left-6 md:left-8 flex items-end" data-id="element-3529">
                {/* Profile Picture */}
                <div className="relative group cursor-pointer mr-6" onClick={() => setIsEditingProfilePic(true)} data-id="element-3530">
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-4 border-navy-900 bg-white/10 flex items-center justify-center shadow-lg" data-id="element-3531">
                    {profilePicturePreview ? <img src={profilePicturePreview} alt={`${teacherName}'s profile`} className="w-full h-full object-cover" data-id="element-3532" /> : <UserIcon className="h-12 w-12 text-gray-400" data-id="element-3533" />}
                  </div>
                  {/* Profile Picture Edit Button */}
                  <div className="absolute bottom-0 right-0 bg-deep-pink p-2 rounded-full shadow-md transition-opacity" title="Edit profile picture" data-id="element-3534">
                    <CameraIcon className="h-4 w-4 text-white" data-id="element-3535" />
                  </div>
                </div>
                {/* Teacher Info */}
                <div data-id="element-3536">
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-white" data-id="element-3537">
                    {teacherName}
                  </h2>
                  <p className="text-xl text-white/80 mb-2" data-id="element-3538">
                    {profileData?.selectedInstruments?.length > 0 ? `${profileData.selectedInstruments[0]} Teacher` : 'Music Teacher'}
                  </p>
                  <div className="flex items-center text-gray-300 text-sm mb-2" data-id="element-3539">
                    <MapPinIcon className="h-4 w-4 mr-1" data-id="element-3540" />
                    <span data-id="element-3541">
                      {profileData?.cityState || 'Location not specified'}
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-3542">
                    <div className="flex mr-2" data-id="element-3543">
                      {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-5 w-5 text-golden fill-current" data-id="element-3544" />)}
                    </div>
                    <span className="text-white font-medium" data-id="element-3545">5.0</span>
                    <span className="text-gray-300 mx-1" data-id="element-3546">·</span>
                    <span className="text-gray-300" data-id="element-3547">0 reviews</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="max-w-4xl mx-auto" data-id="element-3548">
            <div className="mb-8" data-id="element-3549">
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-white" data-id="element-3550">
                Complete Your Profile
              </h1>
              <p className="text-lg text-white/80 mt-2" data-id="element-3551">
                Add details to help students discover you
              </p>
            </div>
            {/* Minimized Info message */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10 mb-4" data-id="element-3552">
              <div className="flex items-center" data-id="element-3553">
                <InfoIcon className="h-4 w-4 text-deep-pink mr-2 flex-shrink-0" data-id="element-3554" />
                <p className="text-gray-300 text-sm" data-id="element-3555">
                  Teachers with complete profiles are{' '}
                  <strong data-id="element-3556">5x more likely</strong> to be discovered by students.
                </p>
              </div>
            </div>
            {/* Error message */}
            {errorMessage && <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center" data-id="element-3557">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-3558" />
                <p className="text-white" data-id="element-3559">{errorMessage}</p>
              </div>}
            {/* Success message */}
            {saveSuccess && <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center" data-id="element-3560">
                <CheckIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" data-id="element-3561" />
                <p className="text-white" data-id="element-3562">
                  Your profile has been saved successfully! Redirecting to your
                  dashboard...
                </p>
              </div>}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10 mb-8" data-id="element-3563">
              <h2 className="text-xl font-serif font-bold text-white mb-6" data-id="element-3564">
                Profile Information
              </h2>
              <div className="space-y-6" data-id="element-3565">
                {/* Full Name */}
                <div data-id="element-3566">
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3567">
                    Full Name
                  </label>
                  <input type="text" id="fullName" value={teacherName} readOnly className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white" data-id="element-3568" />
                  <p className="text-xs text-gray-400 mt-1" data-id="element-3569">
                    This is the name you used during registration.
                  </p>
                </div>
                {/* City & State */}
                <div data-id="element-3570">
                  <label htmlFor="cityState" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3571">
                    City & State
                  </label>
                  <div className="relative" data-id="element-3572">
                    <MapPinIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" data-id="element-3573" />
                    <input type="text" id="cityState" value={profileData.cityState} onChange={handleInputChange} className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="e.g. Los Angeles, CA" data-id="element-3574" />
                  </div>
                </div>
                {/* Full Bio */}
                <div data-id="element-3575">
                  <label htmlFor="fullBio" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3576">
                    Bio / About Me
                  </label>
                  <textarea id="fullBio" value={profileData.fullBio} onChange={handleInputChange} rows={5} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="Tell students about your teaching style, experience, and what they can expect from your lessons..." data-id="element-3577"></textarea>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-3578">
                    Tip: Include your teaching philosophy, notable achievements,
                    and what makes your teaching approach unique.
                  </p>
                </div>
                {/* How I Teach Section - NEW */}
                <div data-id="element-3579">
                  <label htmlFor="howITeach" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3580">
                    How I Teach
                  </label>
                  <textarea id="howITeach" value={profileData.howITeach} onChange={handleInputChange} rows={4} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="Describe your teaching method, approach, and philosophy..." data-id="element-3581"></textarea>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-3582">
                    Share your unique teaching approach. What makes your lessons
                    special? How do you adapt to different learning styles?
                  </p>
                </div>
                {/* Instruments You Teach - Updated Section */}
                <div data-id="element-3583">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3584">
                    Instruments You Teach
                  </label>
                  <p className="text-gray-400 italic text-sm mb-3" data-id="element-3585">
                    If you only teach one instrument, select it below. If you
                    teach multiple, you can add as many as needed.
                  </p>
                  {/* Selected instruments display as tags */}
                  {profileData.selectedInstruments.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-3586">
                      {profileData.selectedInstruments.map(instrument => <div key={instrument} className="bg-deep-pink/20 border border-deep-pink/30 rounded-full px-3 py-1 flex items-center" data-id="element-3587">
                          <span className="text-white text-sm" data-id="element-3588">
                            {instrument}
                          </span>
                          <button type="button" onClick={() => handleRemoveInstrument(instrument)} className="ml-2 text-white/70 hover:text-white" data-id="element-3589">
                            <XIcon className="h-3 w-3" data-id="element-3590" />
                          </button>
                        </div>)}
                    </div>}
                  {/* Custom instrument input */}
                  <form onSubmit={handleAddCustomInstrument} className="mb-3" data-id="element-3591">
                    <div className="flex" data-id="element-3592">
                      <input type="text" value={customInstrument} onChange={e => setCustomInstrument(e.target.value)} className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-l-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="Add a custom instrument..." onKeyDown={e => e.key === 'Enter' && handleAddCustomInstrument(e)} data-id="element-3593" />
                      <button type="submit" className="bg-deep-pink hover:bg-burgundy text-white px-4 py-3 rounded-r-lg" data-id="element-3594">
                        Add
                      </button>
                    </div>
                  </form>
                  {/* Common instruments selection */}
                  <div className="border border-white/20 rounded-lg p-3 max-h-48 overflow-y-auto bg-white/5" data-id="element-3595">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-3596">
                      {instruments.map(instrument => <div key={instrument} className="flex items-center" data-id="element-3597">
                          <input type="checkbox" id={`instrument-${instrument}`} checked={profileData.selectedInstruments.includes(instrument)} onChange={() => handleInstrumentChange(instrument)} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-white/20 rounded bg-white/10" data-id="element-3598" />
                          <label htmlFor={`instrument-${instrument}`} className="ml-2 text-sm text-white cursor-pointer" data-id="element-3599">
                            {instrument}
                          </label>
                        </div>)}
                    </div>
                  </div>
                </div>
                {/* Years of Experience */}
                <div data-id="element-3600">
                  <label htmlFor="yearsExperience" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3601">
                    Years of Experience
                  </label>
                  <input type="number" id="yearsExperience" value={profileData.yearsExperience} onChange={handleInputChange} min="0" max="70" className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="e.g. 5" data-id="element-3602" />
                </div>
                {/* Lesson Format */}
                <div data-id="element-3603">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3604">
                    Lesson Format
                  </label>
                  <div className="flex flex-wrap gap-3" data-id="element-3605">
                    {lessonFormatOptions.map(format => <button key={format} type="button" onClick={() => handleLessonFormatChange(format)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${profileData.lessonFormat.includes(format) ? 'bg-deep-pink text-white' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-3606">
                        {format === 'Online' && <GlobeIcon className="h-4 w-4 mr-2" data-id="element-3607" />}
                        {format === 'In-person' && <MapPinIcon className="h-4 w-4 mr-2" data-id="element-3608" />}
                        {format === 'Both' && <UserCheckIcon className="h-4 w-4 mr-2" data-id="element-3609" />}
                        {format}
                      </button>)}
                  </div>
                </div>
                {/* Pricing Section - UPDATED */}
                <div data-id="element-3610">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3611">
                    Lesson Pricing
                  </label>
                  <p className="text-sm text-gray-400 mb-4" data-id="element-3612">
                    Select the lesson durations you offer and set your rates.
                  </p>
                  {/* Duration Selection - UPDATED */}
                  <div className="mb-4" data-id="element-3613">
                    <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3614">
                      Available Durations
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-3615">
                      {['45', '60'].map(duration => <button key={duration} type="button" onClick={() => handleDurationChange(duration)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${profileData.selectedDurations.includes(duration) ? 'bg-deep-pink text-white' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-3616">
                          {duration} minutes
                        </button>)}
                    </div>
                  </div>
                  {/* Contact for pricing checkbox */}
                  <div className="mb-4" data-id="element-3617">
                    <div className="flex items-center" data-id="element-3618">
                      <input type="checkbox" id="contactForPricing" checked={profileData.contactForPricing} onChange={handleContactForPricingChange} className="h-4 w-4 text-deep-pink focus:ring-deep-pink border-white/20 rounded bg-white/10" data-id="element-3619" />
                      <label htmlFor="contactForPricing" className="ml-2 text-sm text-white cursor-pointer" data-id="element-3620">
                        Contact me for pricing instead
                      </label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-3621">
                      When checked, your profile will display "Contact teacher
                      for pricing" instead of specific rates.
                    </p>
                  </div>
                  {/* Pricing Fields - Only show if contactForPricing is false */}
                  {!profileData.contactForPricing && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3622">
                      {profileData.selectedDurations.includes('45') && <div data-id="element-3623">
                          <label htmlFor="rate45Min" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3624">
                            45-minute lesson (USD)
                          </label>
                          <div className="relative" data-id="element-3625">
                            <DollarSignIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" data-id="element-3626" />
                            <input type="number" id="rate45Min" value={profileData.rate45Min} onChange={handleInputChange} min="0" className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="e.g. 45" data-id="element-3627" />
                          </div>
                        </div>}
                      {profileData.selectedDurations.includes('60') && <div data-id="element-3628">
                          <label htmlFor="rate60Min" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3629">
                            60-minute lesson (USD)
                          </label>
                          <div className="relative" data-id="element-3630">
                            <DollarSignIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" data-id="element-3631" />
                            <input type="number" id="rate60Min" value={profileData.rate60Min} onChange={handleInputChange} min="0" className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="e.g. 60" data-id="element-3632" />
                          </div>
                        </div>}
                    </div>}
                  {/* Show message if contactForPricing is true */}
                  {profileData.contactForPricing && <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-gray-300" data-id="element-3633">
                      <p className="text-sm" data-id="element-3634">
                        Students will be instructed to contact you for pricing
                        information.
                      </p>
                    </div>}
                  {/* Show message if no durations selected */}
                  {profileData.selectedDurations.length === 0 && <p className="text-gray-400 italic text-sm" data-id="element-3635">
                      Select at least one lesson duration to set your pricing.
                    </p>}
                </div>
                {/* Certificates Upload */}
                <div data-id="element-3636">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-3637">
                    Upload Certificates/Degrees (Optional)
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border border-white/20 border-dashed rounded-lg bg-white/5" data-id="element-3638">
                    <div className="space-y-1 text-center" data-id="element-3639">
                      <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-3640" />
                      <div className="flex text-sm text-gray-300" data-id="element-3641">
                        <label htmlFor="certificates" className="relative cursor-pointer bg-transparent rounded-md font-medium text-deep-pink hover:text-burgundy focus-within:outline-none" data-id="element-3642">
                          <span data-id="element-3643">Upload files</span>
                          <input id="certificates" name="certificates" type="file" multiple className="sr-only" accept=".jpg,.jpeg,.png,.pdf" onChange={handleCertificateUpload} data-id="element-3644" />
                        </label>
                        <p className="pl-1" data-id="element-3645">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-400" data-id="element-3646">
                        JPEG, PNG, PDF up to 10MB each
                      </p>
                    </div>
                  </div>
                  {/* Certificate Previews */}
                  {certificates.length > 0 && <div className="mt-4" data-id="element-3647">
                      <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-3648">
                        Uploaded Certificates:
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-id="element-3649">
                        {certificates.map((file, index) => <div key={index} className="relative group border border-white/20 rounded-lg overflow-hidden bg-white/5" data-id="element-3650">
                            <div className="p-2 flex flex-col items-center" data-id="element-3651">
                              {file.type.includes('image') ? <img src={certificatePreviewUrls[index]} alt={`Certificate ${index + 1}`} className="w-full h-24 object-cover rounded-md mb-1" data-id="element-3652" /> : <div className="w-full h-24 flex items-center justify-center rounded-md mb-1" data-id="element-3653">
                                  <FileTextIcon className="h-10 w-10 text-gray-400" data-id="element-3654" />
                                </div>}
                              <p className="text-xs text-gray-300 truncate w-full text-center" data-id="element-3655">
                                {file.name}
                              </p>
                            </div>
                            <button type="button" onClick={() => handleRemoveCertificate(index)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" title="Remove certificate" data-id="element-3656">
                              <XIcon className="h-3 w-3" data-id="element-3657" />
                            </button>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>
            {/* Save Button */}
            <div className="flex items-center justify-end" data-id="element-3658">
              <button onClick={saveProfile} disabled={isSaving} className={`bg-deep-pink hover:bg-burgundy text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center ${isSaving ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-3659">
                {isSaving ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3660">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3661"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3662"></path>
                    </svg>
                    Saving Profile...
                  </> : <>
                    <SaveIcon className="h-5 w-5 mr-2" data-id="element-3663" />
                    Save Profile
                  </>}
              </button>
            </div>
          </div>
        </div>
      </main>
      {/* Image Upload Modals */}
      {(isEditingProfilePic || isEditingBanner) && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-3664">
          <div className="bg-navy-900 rounded-xl max-w-md w-full overflow-hidden" data-id="element-3665">
            <div className="p-6" data-id="element-3666">
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-3667">
                {isEditingProfilePic ? 'Update Profile Picture' : 'Update Banner Image'}
              </h2>
              {/* Upload options */}
              <div className="mb-6" data-id="element-3668">
                {/* Drag and drop area */}
                <div className={`mt-1 flex flex-col justify-center px-6 pt-5 pb-6 border-2 ${isDraggingOver ? 'border-deep-pink' : 'border-white/20'} border-dashed rounded-lg bg-white/5 transition-colors`} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-3669">
                  <div className="space-y-3 text-center" data-id="element-3670">
                    <div className="flex flex-col items-center" data-id="element-3671">
                      {isEditingProfilePic ? <UserIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-3672" /> : <ImageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-3673" />}
                      <p className="mt-2 text-sm text-gray-300" data-id="element-3674">
                        Drag and drop your{' '}
                        {isEditingProfilePic ? 'profile picture' : 'banner image'}{' '}
                        here
                      </p>
                    </div>
                    <div className="flex justify-center" data-id="element-3675">
                      <span className="relative bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg transition-colors cursor-pointer" data-id="element-3676">
                        <span className="flex items-center" data-id="element-3677">
                          <UploadIcon className="h-4 w-4 mr-2" data-id="element-3678" />
                          Choose File
                        </span>
                        <input id={isEditingProfilePic ? 'profile-picture' : 'banner-image'} name={isEditingProfilePic ? 'profile-picture' : 'banner-image'} type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" onChange={isEditingProfilePic ? handleProfilePictureChange : handleBannerImageChange} data-id="element-3679" />
                      </span>
                    </div>
                    <p className="text-xs text-gray-400" data-id="element-3680">
                      {isEditingProfilePic ? 'Recommended: Square image, at least 200x200px' : 'Recommended: Horizontal image, 1200x400px'}
                    </p>
                  </div>
                </div>
                {/* Preview */}
                {(isEditingProfilePic && tempProfilePicture || isEditingBanner && tempBannerImage) && <div className="mt-6 bg-white/5 p-4 rounded-lg" data-id="element-3681">
                    <p className="text-sm font-medium text-white mb-2" data-id="element-3682">
                      Preview:
                    </p>
                    <div className={`${isEditingProfilePic ? 'h-40 w-40 mx-auto rounded-full' : 'h-40 w-full'} overflow-hidden bg-white/5 border border-white/10`} data-id="element-3683">
                      <img src={isEditingProfilePic && tempProfilePicture ? URL.createObjectURL(tempProfilePicture) : isEditingBanner && tempBannerImage ? URL.createObjectURL(tempBannerImage) : ''} alt="Preview" className="w-full h-full object-cover" data-id="element-3684" />
                    </div>
                  </div>}
              </div>
              {/* Action buttons */}
              <div className="flex space-x-3" data-id="element-3685">
                <button onClick={() => {
              setIsEditingProfilePic(false);
              setIsEditingBanner(false);
              setTempProfilePicture(null);
              setTempBannerImage(null);
            }} className="flex-1 bg-transparent hover:bg-white/10 text-white border border-white/30 py-2 px-4 rounded-lg transition-colors flex items-center justify-center" data-id="element-3686">
                  <XIcon className="h-4 w-4 mr-2" data-id="element-3687" />
                  Cancel
                </button>
                <button onClick={() => {
              if (isEditingProfilePic && tempProfilePicture) {
                setProfilePicture(tempProfilePicture);
                const previewUrl = URL.createObjectURL(tempProfilePicture);
                if (profilePicturePreview && !profilePicturePreview.startsWith('http')) {
                  URL.revokeObjectURL(profilePicturePreview);
                }
                setProfilePicturePreview(previewUrl);
              } else if (isEditingBanner && tempBannerImage) {
                setBannerImage(tempBannerImage);
                const previewUrl = URL.createObjectURL(tempBannerImage);
                if (bannerImagePreview && !bannerImagePreview.startsWith('http')) {
                  URL.revokeObjectURL(bannerImagePreview);
                }
                setBannerImagePreview(previewUrl);
              }
              setIsEditingProfilePic(false);
              setIsEditingBanner(false);
              setTempProfilePicture(null);
              setTempBannerImage(null);
            }} disabled={!(tempProfilePicture || tempBannerImage)} className={`flex-1 bg-deep-pink hover:bg-burgundy text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center ${!(tempProfilePicture || tempBannerImage) ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-3688">
                  <CheckIcon className="h-4 w-4 mr-2" data-id="element-3689" />
                  Apply Image
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center" data-id="element-3690">
                Note: Image will be saved when you click "Save Profile" at the
                bottom of the page
              </p>
            </div>
          </div>
        </div>}
      <Footer data-id="element-3691" />
    </div>;
};